import React, { useCallback, useState } from "react";
import * as Yup from "yup";
import styled from "styled-components/macro";
import { Formik } from "formik";

import {
  Box,
  Button as MuiButton,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField as MuiTextField,
} from "@material-ui/core";

import { Alert as MuiAlert } from "@material-ui/lab";

import { spacing, SpacingProps } from "@material-ui/system";
import { Close } from "@material-ui/icons";
import { AdminApi, CreateCourseModel } from "../../backendIntegration/AdminApi";

const Alert = styled(MuiAlert)(spacing);
const CloseIconButton = styled(IconButton)`
  position: absolute;
  right: 6px;
  top: 6px;
`;

const TextField = styled(MuiTextField)<{ my?: number }>(spacing);

interface ButtonPropstype extends SpacingProps {
  component?: string;
}

const Button = styled(MuiButton)<ButtonPropstype>(spacing);

const initialValues: CreateCourseModel = {
  name: "",
  description: "",
  priceEur: 0,
  externalId: "",
  category: "",
};

const validationSchema = Yup.object().shape({
  name: Yup.string().required("Required"),
  priceEur: Yup.number().required("Required"),
  externalId: Yup.string().required("Required"),
  category: Yup.string().required("Required"),
});

interface CreateCourseDialogProps {
  open: boolean;
  onClose: () => void;
}
export const CreateCourseDialog: React.FC<CreateCourseDialogProps> = ({
  open,
  onClose,
}) => {
  const [status, setStatus] = useState<boolean | null>(null);
  const hideStatusAfterWhile = useCallback(() => {
    const timer = setTimeout(() => {
      setStatus(null);
    }, 4000);
    return () => {
      clearTimeout(timer);
    };
  }, []);

  const adminApi = new AdminApi();
  const createUser = async (values: CreateCourseModel) => {
    const response = await adminApi.createCourse(values);
    return response;
  };

  const handleSubmit = async (
    values: CreateCourseModel,
    { resetForm, setErrors, setSubmitting }: any
  ) => {
    try {
      setStatus(null);
      const isCreatedSuccessfully = await createUser(values);
      setSubmitting(false);
      if (isCreatedSuccessfully) {
        resetForm();
        setStatus(true);
        hideStatusAfterWhile();
      } else {
        setErrors({ submit: "Course was not created." });
        setStatus(false);
        hideStatusAfterWhile();
      }
    } catch (error) {
      setStatus(false);
      hideStatusAfterWhile();
      setErrors({ submit: error.message });
      setSubmitting(false);
    }
  };
  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        isSubmitting,
        touched,
        values,
      }) => (
        <Dialog
          open={open}
          onClose={onClose}
          aria-labelledby="form-dialog-title"
        >
          <form onSubmit={handleSubmit}>
            <DialogTitle id="form-dialog-title">
              Create new Course
              {!isSubmitting && (
                <CloseIconButton aria-label="Close" onClick={onClose}>
                  <Close fontSize="small" />
                </CloseIconButton>
              )}
            </DialogTitle>
            {isSubmitting ? (
              <Box display="flex" justifyContent="center" my={6}>
                <CircularProgress />
              </Box>
            ) : (
              <DialogContent>
                {status === true ? (
                  <Alert severity="success" my={3}>
                    The course has been created successfully!
                  </Alert>
                ) : status === false ? (
                  <Alert severity="error" my={3}>
                    An error has occured, please try again.
                  </Alert>
                ) : null}
                <TextField
                  name="name"
                  label="Course Name"
                  value={values.name}
                  error={Boolean(touched.name && errors.name)}
                  fullWidth
                  helperText={touched.name && errors.name}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  type="text"
                  variant="outlined"
                  my={2}
                />

                <TextField
                  name="priceEur"
                  label="Price in EUR"
                  value={values.priceEur}
                  error={Boolean(touched.priceEur && errors.priceEur)}
                  fullWidth
                  helperText={touched.priceEur && errors.priceEur}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  type="number"
                  variant="outlined"
                  my={2}
                />

                <TextField
                  name="externalId"
                  label="External ID"
                  value={values.externalId}
                  error={Boolean(touched.externalId && errors.externalId)}
                  fullWidth
                  helperText={touched.externalId && errors.externalId}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  type="text"
                  variant="outlined"
                  my={2}
                />

                <TextField
                  name="category"
                  label="Category"
                  value={values.category}
                  error={Boolean(touched.category && errors.category)}
                  fullWidth
                  helperText={touched.category && errors.category}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  type="text"
                  variant="outlined"
                  my={2}
                />

                <TextField
                  name="description"
                  label="Intended for"
                  value={values.description}
                  error={Boolean(touched.description && errors.description)}
                  fullWidth
                  helperText={touched.description && errors.description}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  type="text"
                  multiline
                  rows={3}
                  variant="outlined"
                  my={2}
                />
                <DialogActions>
                  <Button type="submit" variant="contained" color="primary">
                    Submit
                  </Button>
                </DialogActions>
              </DialogContent>
            )}
          </form>
        </Dialog>
      )}
    </Formik>
  );
};
